import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { DecimalPipe } from 'app/shared/decimal.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { UIModalComponent } from 'app/ui/ui-modal.component';
import { ModalSelectProjectComponent } from '../modal-select-project/modal-select-project.component';
import { ModalSelectAddressComponent } from '../modal-select-address/modal-select-address.component';
import { Util } from 'app/shared/util';

declare const Mangler: any;

@Component({
	selector: 'modal-edit-work-order',
	templateUrl: './modal-edit-work-order.component.html'
})
export class ModalEditWorkOrderComponent extends UIModalBaseComponent {

	@ViewChild(ModalSelectProjectComponent) projectSelectModal: ModalSelectProjectComponent;
	@ViewChild(ModalSelectAddressComponent) addressPickerModal: ModalSelectAddressComponent;
	@ViewChild('selectJobTemplateModal') selectJobTemplateModal: UIModalComponent;
	@ViewChild('saveAsJobTemplateModal') saveAsJobTemplateModal: UIModalComponent;
	@ViewChild('multiselectdropdown') multiselectdropdown: ElementRef;

	id;
	details;
	addresses = [];
	disabled = false;
	project_id = null;
	mode = 'edit';

	selectedLabels = [];
	labelList = [];
	isDropdownOpen = false;

	jobTemplates;
	templateName = '';
	templateTasks: any[] = [];
	templatePhases: any[] = [];
	templateDependencies: any[] = [];
	templateData: any = {};
	editingTemplateId = null;

	get billable() { return !!this.details.record.billable; }
	set billable(value) { this.details.record.billable = value ? 1 : 0; }

	get auto_complete() { return !!this.details.record.auto_complete; }
	set auto_complete(value) { this.details.record.auto_complete = value ? 1 : 0; }

	get hasMatchingAddress() {
		if (!this.addresses) return false;
		if (!this.app.hasAddress(this.details.record)) return false;
		if (!this.addresses) return false;
		for (const contact of this.addresses) {
			if (this.app.compareAddress(this.details.record, contact)) return true;
		}
		return false;
	};

	get hasAddress() {
		if (!this.addresses) return false;
		for (const contact of this.addresses) {
			if (this.app.hasAddress(contact)) return true;
		}
		return false;
	}

	constructor(
		public app: AppService,
		private api: ApiService,
	) {
		super();
	}

	open(mdata: any) {
		this.id = mdata.id || 'new';
		this.project_id = mdata.project_id || null;
		this.mode = mdata.mode || 'edit';
		this.details = null;
		this.disabled = true;
		this.addresses = [];
		this.jobTemplates = null;
		this.selectedLabels = [];
		this.labelList = [];
		this.isDropdownOpen = false;

		if (this.id === 'new') {
			// Offer job templates if the org has any
			this.api.task.listWorkOrderTemplates(list => {
				if (list?.length) {
					this.disabled = false;
					this.jobTemplates = list;
					this.selectJobTemplateModal.open();
				} else {
					this.loadNew();
				}
			}, () => {
				this.loadNew();
			});
		} else {
			this.api.task.getWorkOrder(this.id, data => {
				this.disabled = false;
				this.details = data;
				this.applyLabels();
				this.projectChanged();
				this.modal.open();
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	loadNew(templateId = null) {
		const payload: any = { project: this.project_id };
		if (templateId) payload.work_order_template_id = templateId;

		this.disabled = true;
		this.api.task.newWorkOrder(payload, data => {
			this.disabled = false;
			this.details = data;
			this.applyLabels();
			// New-definition tasks store hours; show minutes where needed
			if (Array.isArray(this.details.record.tasks)) {
				this.details.record.tasks.forEach(task => { if (!task.id && task.duration_type === 'min') task.duration *= 60; });
			}
			if (this.project_id) this.projectChanged();
			if (this.selectJobTemplateModal?.isOpen) this.selectJobTemplateModal.close();
			this.modal.open();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	selectTemplate(template) {
		this.loadNew(template?.id);
	}

	applyLabels() {
		this.selectedLabels = Mangler.clone(this.details.record.labels || []);
		// Only offer labels that aren't already selected
		this.labelList = Mangler.clone(this.details.label_list || [])
			.filter(label => !this.selectedLabels.some(selectedLabel => selectedLabel.id === label.id));
	}

	toggleSelection(label: any) {
		const index = this.selectedLabels.findIndex(selectedLabel => selectedLabel.id === label.id);
		if (index !== -1) {
			// Remove the label from selectedLabels and add it back to labelList
			this.selectedLabels.splice(index, 1);
			this.labelList.push(label);
		} else {
			// Add the label to selectedLabels and remove it from labelList
			this.selectedLabels.push(label);
			const labelIndex = this.labelList.findIndex(item => item.id === label.id);
			if (labelIndex !== -1) this.labelList.splice(labelIndex, 1);
		}
	}

	toggleDropdown() {
		this.isDropdownOpen = !this.isDropdownOpen;
	}

	@HostListener('document:click', ['$event'])
	handleClickOutside(event: Event) {
		if (this.multiselectdropdown && !this.multiselectdropdown.nativeElement.contains(event.target)) {
			this.isDropdownOpen = false;
		}
	}

	saveButton() {
		if (!this.details.record.project_id) {
			this.app.notifications.showDanger('Please select a Project');
			return;
		}

		if (!this.details.record.name) {
			this.app.notifications.showDanger('Please enter the Job name');
			return;
		}

		if (!this.app.hasAddress(this.details.record)) {
			this.app.notifications.showDanger('Please enter the Job Location');
			return;
		}

		// Send a clone so converting task durations to hours does not disturb the live inputs on error
		this.details.record.labels = this.selectedLabels;
		const payload = Mangler.clone(this.details.record);
		if (this.id === 'new' && Array.isArray(payload.tasks)) {
			payload.tasks = payload.tasks
				.filter(task => (task.name || '').trim())
				.map(task => {
					if (!task.id && task.duration_type === 'min') task.duration = DecimalPipe.parse(task.duration) / 60;
					else task.duration = DecimalPipe.parse(task.duration);
					return task;
				});
		}

		this.disabled = true;
		this.api.task.saveWorkOrder(payload, data => {
			this.disabled = false;
			this.app.notifications.showSuccess(this.id === 'new' ? 'Job created' : 'Job updated.');
			// Blank the live address inputs before the modal closes so Chrome cannot
			// offer to save the job address as the user's personal address. The
			// payload was already sent above, so the save is unaffected.
			Util.blankAddressFields(this.details.record);
			this.modal.close(data);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	saveAsTemplate() {
		this.editingTemplateId = null;
		this.templateName = '';
		this.templatePhases = this.details.phases || [];
		this.templateDependencies = this.details.dependencies || [];
		this.templateTasks = (this.details.record.tasks || []).map(task => {
			const clone = Mangler.clone(task);
			if (clone.id && clone.duration_type === 'min') clone.duration *= 60;
			clone.id = null;
			clone.type = 'simple';
			return clone;
		});
		this.saveAsJobTemplateModal.open();
	}

	editTemplate(template) {
		this.disabled = true;
		this.api.task.getWorkOrderTemplate(template.id, data => {
			this.disabled = false;
			const tpl = data.template;
			this.editingTemplateId = template.id;
			this.templateName = tpl.template_name || '';
			this.templatePhases = data.phases || [];
			this.templateDependencies = data.dependencies || [];
			this.templateData = {
				name: tpl.name || '',
				type: tpl.type || 'contract',
				billable: tpl.billable ? 1 : 0,
				notes: tpl.notes || '',
				address_line_1: tpl.address_line_1 || '',
				address_line_2: tpl.address_line_2 || '',
				address_line_3: tpl.address_line_3 || '',
				posttown: tpl.posttown || '',
				county: tpl.county || '',
				postcode: tpl.postcode || '',
				country: tpl.country || ''
			};
			this.templateTasks = (tpl.tasks || []).map(task => {
				const clone = Mangler.clone(task);
				if (clone.duration_type === 'min') clone.duration *= 60; // stored hours -> display minutes
				clone.id = null;
				clone.type = 'simple';
				return clone;
			});
			this.saveAsJobTemplateModal.open();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	saveJobTemplate() {
		const tasks = this.templateTasks
			.filter(task => (task.name || '').trim())
			.map(task => {
				const clone = Mangler.clone(task);
				clone.duration = clone.duration_type === 'min' ? DecimalPipe.parse(clone.duration) / 60 : DecimalPipe.parse(clone.duration);
				return clone;
			});

		this.disabled = true;

		if (this.editingTemplateId) {
			const td = this.templateData;
			this.api.task.updateWorkOrderTemplate({
				id: this.editingTemplateId,
				template_name: this.templateName,
				name: td.name,
				type: td.type,
				billable: td.billable ? 1 : 0,
				notes: td.notes,
				address_line_1: td.address_line_1,
				address_line_2: td.address_line_2,
				address_line_3: td.address_line_3,
				posttown: td.posttown,
				county: td.county,
				postcode: td.postcode,
				country: td.country,
				tasks
			}, () => {
				this.disabled = false;
				this.app.notifications.showSuccess('Job template updated.');
				this.saveAsJobTemplateModal.close();
				this.api.task.listWorkOrderTemplates(list => { this.jobTemplates = list; }, () => {});
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
			return;
		}

		const r = this.details.record;
		const payload = {
			template_name: this.templateName,
			name: r.name,
			type: r.type,
			billable: r.billable ? 1 : 0,
			notes: r.notes,
			address_line_1: r.address_line_1,
			address_line_2: r.address_line_2,
			address_line_3: r.address_line_3,
			posttown: r.posttown,
			county: r.county,
			postcode: r.postcode,
			country: r.country,
			tasks
		};

		this.api.task.createWorkOrderTemplate(payload, () => {
			this.disabled = false;
			this.app.notifications.showSuccess('Job template added.');
			this.saveAsJobTemplateModal.close();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	deleteTemplate(template) {
		this.app.dialog.pop('Delete Job Template', 'Are you sure you want to delete "' + template.template_name + '" job template?', ['Cancel', '*Delete'], btn => {
			if (btn === 1) {
				this.disabled = true;
				this.api.task.deleteWorkOrderTemplate(template.id, () => {
					this.app.notifications.showSuccess('Job template deleted.');
					this.api.task.listWorkOrderTemplates(list => {
						this.disabled = false;
						this.jobTemplates = list;
					}, () => {
						this.disabled = false;
					});
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	addTask(list) {
		list.push({ id: null, type: 'simple', name: '', description: '', phase_id: null, duration: 0, duration_type: 'hour', billable: 0, dependencies: [] });
	}

	removeTask(list, index) {
		list.splice(index, 1);
	}

	taskDependencyChange(data, task) {
		task.dependencies = data;
	}

	taskDurationChanged(task) {
		task.duration = DecimalPipe.transform(task.duration, 0, 4);
	}

	delete() {
		this.app.dialog.pop('Delete Job', 'Are you sure you want to delete this job?', ['Cancel', '*Delete'], button => {
			if (button === 1) {
				this.disabled = true;
				this.api.task.deleteWorkOrder(this.details.record.id, () => {
					this.disabled = false;
					this.app.notifications.showSuccess('Job deleted.');
					this.modal.close('deleted');
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	useQuoteAddress(addr) {
		if (!this.details) return false;

		this.details.record.address_line_1 = addr.address_line_1;
		this.details.record.address_line_2 = addr.address_line_2;
		this.details.record.address_line_3 = addr.address_line_3;
		this.details.record.posttown = addr.posttown;
		this.details.record.county = addr.county;
		this.details.record.postcode = addr.postcode;
		this.details.record.country = addr.country;
		this.details.record.address_name = addr.name || addr.description || addr.title || '';
	}

	reopen() {
		this.app.dialog.pop('Reopen Job', 'Are you sure you want to reopen this job?', ['Cancel', '*Reopen'], button => {
			if (button === 1) {
				this.disabled = true;
				this.api.task.reopenWorkOrder(this.details.record.id, data => {
					this.disabled = false;
					this.app.notifications.showSuccess('Job Reopened.');
					this.modal.close(data);
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	projectChanged() {
		if (this.details.record.project_id) {
			this.api.task.getProjectAddress({ project_ids: [this.details.record.project_id] }, data => {
				this.addresses = data;
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	projectSelectModalClosed(data) {
		if (data) {
			this.details.record.project = data.description;
			this.details.record.project_id = data.id;
			this.projectChanged();
		}
	}

	openAddressPicker() {
		if (!this.addresses?.length) return;
		const groups = this.app.buildAddressGroups(this.addresses);
		this.addressPickerModal.open({
			addressGroups: groups,
			preselectAddress: this.app.extractAddressFields(this.details.record, '')
		});
	}

	addressPickerClosed(sel: any | null) {
		if (!sel) return;
		this.useQuoteAddress(sel);
	}

}
